/**
 * Money coming in: taking a payment, working the collections queue, and
 * running the dues.
 *
 * The dues run screen is deliberately a two-step with a wide gap between
 * the buttons. Committing is four hundred entries in an immutable ledger,
 * and it should not sit next to "preview" looking like the same kind of
 * thing.
 */

import { useMemo, useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { date, dateTime, money, parseMoney, titleCase } from '../lib/format.ts';
import {
  BylawNotices, Card, Empty, ErrorBanner, Loading, Money, Status, Tile,
} from '../components/ui.tsx';

/* ================================================================== */
interface OpenItem {
  ar_transaction_id: string;
  accounting_date: string;
  txn_type: string;
  description: string | null;
  amount_minor: number;
  allocated_minor: number;
  open_minor: number;
}

interface CollectionsRow {
  member_account_id: string;
  account_number: string;
  display_name: string;
  status: string;
  escalation_hold_until: string | null;
  autopay_enabled: boolean;
  overdue_minor: number;
  oldest_unpaid_on: string;
  days_overdue: number;
  open_item_count: number;
  has_payment_method: boolean;
}

interface BillingRun {
  id: string;
  run_type: string;
  service_from: string;
  service_to: string;
  charge_date: string;
  status: 'preview' | 'committed' | 'reversed' | 'failed';
  membership_count: number;
  total_minor: number;
  created_at: string;
  committed_at: string | null;
  reversed_at: string | null;
  notes: string | null;
}

interface RunItem {
  id: string;
  account_number: string;
  display_name: string;
  category_name: string;
  gross_minor: number;
  proration_numerator: number | null;
  proration_denominator: number | null;
  net_minor: number;
  skip_reason: string | null;
  ar_transaction_id: string | null;
}

const CHANNELS = [
  { value: 'check', label: 'Check' },
  { value: 'front_desk', label: 'Cash at the Desk' },
  { value: 'lockbox', label: 'Lockbox' },
  { value: 'wire', label: 'Wire / ACH' },
  { value: 'member_app', label: 'Card on File' },
] as const;

/* ================================================================== */
export function TakePaymentPage() {
  const { currency } = useSession();
  const [search, setSearch] = useState('');
  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [channel, setChannel] = useState<string>('check');
  const [reference, setReference] = useState('');
  const [methodId, setMethodId] = useState('');
  const [taken, setTaken] = useState<Record<string, unknown> | null>(null);

  const accounts = useApi<{ accounts: { member_account_id: string; account_number: string; display_name: string; balance_minor: number }[] }>(
    `/v1/member-accounts?search=${encodeURIComponent(search)}&limit=25`,
    [search],
  );
  const openItems = useApi<{ open_items: OpenItem[] }>(
    accountId ? `/v1/member-accounts/${accountId}/open-items` : null,
    [accountId],
  );
  const methods = useApi<{ payment_methods: { id: string; display_brand: string | null; display_last4: string | null }[] }>(
    accountId ? `/v1/member-accounts/${accountId}/payment-methods` : null,
    [accountId],
  );

  const take = useMutation(async (body: Record<string, unknown>) =>
    api('/v1/payments/take', {
      method: 'POST',
      body,
      // A staff member who taps twice on a slow connection must not take the
      // money twice. The key is minted per attempt, not per render.
      idempotencyKey: crypto.randomUUID(),
    }),
  );

  const amountMinor = parseMoney(amount, currency);
  const owed = (openItems.data?.open_items ?? []).reduce((a, i) => a + i.open_minor, 0);
  const needsCard = channel === 'member_app';

  const submit = async () => {
    if (!accountId || !amountMinor) return;
    const result = await take.run({
      member_account_id: accountId,
      amount_minor: amountMinor,
      channel,
      reference: reference || undefined,
      payment_method_id: needsCard && methodId ? methodId : undefined,
    });
    if (result) {
      setTaken(result as Record<string, unknown>);
      setAmount('');
      setReference('');
      openItems.reload();
      accounts.reload();
    }
  };

  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Take a Payment</h1>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 18 }}>
        Cash and checks settle as you record them. A card is authorized now and posts to the
        ledger when the processor confirms it — usually within a day.
      </p>

      <div className="grid-2">
        <div>
          <Card title="Payment">
            <ErrorBanner error={take.error} />

            <label className="field">
              <span>Find the member</span>
              <input
                placeholder="Name or account number"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>

            <label className="field">
              <span>Account</span>
              <select value={accountId} onChange={(e) => { setAccountId(e.target.value); setTaken(null); }}>
                <option value="">Choose an account…</option>
                {(accounts.data?.accounts ?? []).map((a) => (
                  <option key={a.member_account_id} value={a.member_account_id}>
                    {a.account_number} · {a.display_name} · {money(a.balance_minor, currency)}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>How it arrived</span>
              <select value={channel} onChange={(e) => setChannel(e.target.value)}>
                {CHANNELS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </label>

            {needsCard && (
              <label className="field">
                <span>Card on file</span>
                <select value={methodId} onChange={(e) => setMethodId(e.target.value)}>
                  <option value="">Choose a card…</option>
                  {(methods.data?.payment_methods ?? []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.display_brand ?? 'Card'} ····{m.display_last4 ?? '????'}
                    </option>
                  ))}
                </select>
                {(methods.data?.payment_methods ?? []).length === 0 && accountId && (
                  <span className="muted small">
                    This member has no card on file. Take it another way, or ask them to add one.
                  </span>
                )}
              </label>
            )}

            <label className="field">
              <span>Amount</span>
              <input
                placeholder={owed > 0 ? money(owed, currency) : '0.00'}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
              />
              {amount && amountMinor === null && (
                <span className="muted small">That is not an amount this currency can hold.</span>
              )}
            </label>

            {owed > 0 && (
              <button
                className="secondary small"
                style={{ marginBottom: 13 }}
                onClick={() => setAmount((owed / 10 ** currency.exponent).toFixed(currency.exponent))}
              >
                Pay the full {money(owed, currency)}
              </button>
            )}

            <label className="field">
              <span>Reference</span>
              <input
                placeholder="Check number, deposit slip…"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </label>

            <button
              disabled={take.busy || !accountId || !amountMinor || (needsCard && !methodId)}
              onClick={submit}
            >
              {take.busy ? 'Taking…' : amountMinor ? `Take ${money(amountMinor, currency)}` : 'Take payment'}
            </button>
          </Card>

          {taken && (
            <Card title="Recorded">
              <p style={{ marginTop: 0 }}>
                {taken.status === 'settled' ? (
                  <Status tone="good">
                    {money(Number(taken.amount_minor), currency)} received and applied.
                  </Status>
                ) : (
                  <Status tone="neutral">
                    {money(Number(taken.amount_minor), currency)} authorized. It will post to the
                    ledger when the processor confirms it.
                  </Status>
                )}
              </p>
              {Number(taken.fee_minor ?? 0) > 0 && (
                <p className="muted small" style={{ marginBottom: 0 }}>
                  The processor keeps {money(Number(taken.fee_minor), currency)}. The member is
                  credited the full amount; the fee is the club's expense.
                </p>
              )}
            </Card>
          )}
        </div>

        <div>
          <Card title="What This Member Owes" flush>
            {!accountId ? (
              <Empty>Choose an account.</Empty>
            ) : openItems.loading && !openItems.data ? (
              <div style={{ padding: 16 }}><Loading rows={3} /></div>
            ) : (openItems.data?.open_items.length ?? 0) === 0 ? (
              <Empty>Nothing outstanding.</Empty>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>What</th>
                    <th className="num">Charged</th>
                    <th className="num">Still owed</th>
                  </tr>
                </thead>
                <tbody>
                  {openItems.data!.open_items.map((i) => (
                    <tr key={i.ar_transaction_id}>
                      <td>{date(i.accounting_date)}</td>
                      <td>{i.description ?? titleCase(i.txn_type)}</td>
                      <td className="num"><Money minor={i.amount_minor} currency={currency} /></td>
                      <td className="num"><Money minor={i.open_minor} currency={currency} /></td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={3}><strong>Total owed</strong></td>
                    <td className="num"><strong>{money(owed, currency)}</strong></td>
                  </tr>
                </tbody>
              </table>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

/* ================================================================== */
export function CollectionsPage() {
  const { currency, atLeast } = useSession();
  const queue = useApi<{ accounts: CollectionsRow[] }>('/v1/collections');
  const unapplied = useApi<{ unapplied: Record<string, unknown>[] }>('/v1/payments/unapplied');

  const lateFees = useMutation(async () => api('/v1/collections/late-fees', { method: 'POST', body: {} }));
  const escalate = useMutation(async () => api('/v1/collections/escalate', { method: 'POST', body: {} }));
  const autopay = useMutation(async () => api('/v1/collections/autopay-run', { method: 'POST', body: {} }));
  const hold = useMutation(async (id: string, until: string | null) =>
    api(`/v1/collections/${id}/hold`, { method: 'POST', body: { until } }),
  );

  const reload = () => { queue.reload(); unapplied.reload(); };
  const rows = queue.data?.accounts ?? [];

  const totals = useMemo(() => ({
    overdue: rows.reduce((a, r) => a + r.overdue_minor, 0),
    over60: rows.filter((r) => r.days_overdue > 60).reduce((a, r) => a + r.overdue_minor, 0),
    suspended: rows.filter((r) => r.status === 'suspended' || r.status === 'collections').length,
    onHold: rows.filter((r) => r.escalation_hold_until).length,
  }), [rows]);

  if (queue.loading && !queue.data) return <Loading rows={6} />;
  if (queue.error) return <ErrorBanner error={queue.error} />;

  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Collections</h1>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 18 }}>
        Everyone with something unpaid, oldest first. A member goes up one rung of the ladder per
        run — never straight from a reminder to being refused at the bar.
      </p>

      {/* The club's own forfeiture rule, where it applies. The software
          does not enforce Article X — it shows it to whoever is working
          the queue, which is the whole point of a notice. */}
      <BylawNotices surface="collections" />

      {/* Article X's last rung, and deliberately above the queue rather
          than inside it: the queue is worked daily by whoever chases
          payment, this is read by whoever signs the letters. It renders
          nothing at all until a club has adopted a forfeiture rule. */}
      <ForfeitureWatch />

      <Card
        title="Where It Stands"
        actions={
          atLeast('controller') && (
            <div className="row">
              <button className="secondary small" disabled={autopay.busy} onClick={() => autopay.run().then(reload)}>
                {autopay.busy ? 'Queueing…' : 'Run autopay'}
              </button>
              <button className="secondary small" disabled={lateFees.busy} onClick={() => lateFees.run().then(reload)}>
                {lateFees.busy ? 'Charging…' : 'Charge late fees'}
              </button>
              <button className="small" disabled={escalate.busy} onClick={() => escalate.run().then(reload)}>
                {escalate.busy ? 'Escalating…' : 'Escalate overdue'}
              </button>
            </div>
          )
        }
      >
        <ErrorBanner error={lateFees.error ?? escalate.error ?? autopay.error} />

        <div className="tiles">
          <Tile label="Total overdue" value={money(totals.overdue, currency)} note={`${rows.length} accounts`} />
          <Tile
            label="More than 60 days"
            value={money(totals.over60, currency)}
            note={totals.over60 > 0 ? 'worth a phone call' : 'nothing that old'}
            tone={totals.over60 > 0 ? 'serious' : 'good'}
          />
          <Tile
            label="Suspended or in collections"
            value={String(totals.suspended)}
            tone={totals.suspended > 0 ? 'critical' : 'good'}
            note={totals.suspended > 0 ? 'refused at outlets' : 'nobody'}
          />
          <Tile label="On hold" value={String(totals.onHold)} note="left alone deliberately" />
        </div>

        {(unapplied.data?.unapplied.length ?? 0) > 0 && (
          <p style={{ marginBottom: 0 }}>
            <Status tone="warning">
              {unapplied.data!.unapplied.length} settled payment
              {unapplied.data!.unapplied.length === 1 ? ' has' : 's have'} money not yet applied to
              anything. Usually a payment that arrived without a reference.
            </Status>
          </p>
        )}
      </Card>

      <Card title="Who Owes What" flush>
        {rows.length === 0 ? (
          <Empty>Nothing outstanding anywhere. Unusual, and worth enjoying.</Empty>
        ) : (
          <table className="table">
            <colgroup>
              <col style={{ width: '26%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '12%' }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>Account</th>
                <th>Standing</th>
                <th className="num">Overdue</th>
                <th className="num" style={{ paddingLeft: 18 }}>Days</th>
                <th style={{ paddingLeft: 18 }}>Since</th>
                <th>Can pay</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.member_account_id}>
                  <td>
                    <strong>{r.display_name}</strong>
                    <div className="muted small">{r.account_number}</div>
                  </td>
                  <td>
                    <Status
                      tone={
                        r.status === 'collections' ? 'critical'
                        : r.status === 'suspended' ? 'serious'
                        : r.status === 'past_due' ? 'warning'
                        : 'good'
                      }
                    >
                      {titleCase(r.status)}
                    </Status>
                    {r.escalation_hold_until && (
                      <div className="muted small">on hold until {date(r.escalation_hold_until)}</div>
                    )}
                  </td>
                  <td className="num"><Money minor={r.overdue_minor} currency={currency} /></td>
                  <td className="num" style={{ paddingLeft: 18 }}>{r.days_overdue}</td>
                  <td style={{ paddingLeft: 18 }}>{date(r.oldest_unpaid_on)}</td>
                  <td>
                    {r.autopay_enabled && r.has_payment_method
                      ? <Status tone="good">autopay</Status>
                      : r.has_payment_method
                        ? <span className="muted">card on file</span>
                        : <span className="muted">no card</span>}
                  </td>
                  <td className="num">
                    {atLeast('manager') && (
                      <button
                        className="secondary small"
                        disabled={hold.busy}
                        onClick={() =>
                          hold
                            .run(
                              r.member_account_id,
                              r.escalation_hold_until
                                ? null
                                : new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
                            )
                            .then(reload)
                        }
                      >
                        {r.escalation_hold_until ? 'Release' : 'Hold 30 days'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ================================================================== */
export function BillingRunsPage() {
  const { currency, atLeast } = useSession();
  const runs = useApi<{ runs: BillingRun[] }>('/v1/billing-runs');
  const [openId, setOpenId] = useState<string | null>(null);
  const detail = useApi<BillingRun & { items: RunItem[] }>(
    openId ? `/v1/billing-runs/${openId}` : null,
    [openId],
  );

  const [from, setFrom] = useState(defaultMonth().from);
  const [to, setTo] = useState(defaultMonth().to);

  const preview = useMutation(async () =>
    api('/v1/billing-runs', {
      method: 'POST',
      body: { service_starts_on: from, service_ends_on: to, charge_date: from },
    }),
  );
  const commit = useMutation(async (id: string) => api(`/v1/billing-runs/${id}/commit`, { method: 'POST' }));
  const reverse = useMutation(async (id: string, reason: string) =>
    api(`/v1/billing-runs/${id}/reverse`, { method: 'POST', body: { reason } }),
  );

  const items = detail.data?.items ?? [];
  const billed = items.filter((i) => !i.skip_reason && i.net_minor > 0);
  const skipped = items.filter((i) => i.skip_reason);
  const prorated = billed.filter((i) => i.proration_numerator !== null);

  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Dues Runs</h1>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 18 }}>
        A run is calculated as a preview first. Nothing is charged and nothing reaches the ledger
        until you commit it — which cannot be undone except by reversing the whole run.
      </p>

      {atLeast('controller') && (
        <Card title="New Run">
          <ErrorBanner error={preview.error} />
          <div className="row">
            <label className="field" style={{ marginBottom: 0 }}>
              <span>Service from</span>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="field" style={{ marginBottom: 0 }}>
              <span>Service to</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
            <button
              disabled={preview.busy}
              onClick={() => preview.run().then((r) => { if (r) { runs.reload(); setOpenId((r as BillingRun).id); } })}
            >
              {preview.busy ? 'Calculating…' : 'Preview'}
            </button>
          </div>
          <p className="muted small" style={{ marginBottom: 0, marginTop: 12 }}>
            Dues for a period running past the end of the charge month are booked as deferred
            revenue and earned month by month, not recognized all at once.
          </p>
        </Card>
      )}

      <Card title="Runs" flush>
        {runs.loading && !runs.data ? (
          <div style={{ padding: 16 }}><Loading rows={3} /></div>
        ) : (runs.data?.runs.length ?? 0) === 0 ? (
          <Empty>No dues run yet.</Empty>
        ) : (
          <table className="table">
            <colgroup>
              <col style={{ width: '30%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '14%' }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>Service period</th>
                <th>Type</th>
                <th style={{ paddingLeft: 14 }}>Charged</th>
                <th>Status</th>
                <th className="num">Members</th>
                <th className="num">Total</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {runs.data!.runs.map((r) => (
                <tr key={r.id}>
                  <td>{date(r.service_from)} – {date(r.service_to)}</td>
                  <td>{titleCase(r.run_type)}</td>
                  <td style={{ paddingLeft: 14 }}>{date(r.charge_date)}</td>
                  <td>
                    <Status
                      tone={
                        r.status === 'committed' ? 'good'
                        : r.status === 'preview' ? 'neutral'
                        : r.status === 'reversed' ? 'warning'
                        : 'critical'
                      }
                    >
                      {r.status}
                    </Status>
                  </td>
                  <td className="num">{r.membership_count}</td>
                  <td className="num"><Money minor={r.total_minor} currency={currency} /></td>
                  <td className="num">
                    <button className="secondary small" onClick={() => setOpenId(r.id === openId ? null : r.id)}>
                      {r.id === openId ? 'Close' : 'Open'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {openId && detail.data && (
        <Card
          title={`${date(detail.data.service_from)} – ${date(detail.data.service_to)}`}
          actions={
            atLeast('controller') && detail.data.status === 'preview' ? (
              <button
                disabled={commit.busy}
                onClick={() =>
                  commit.run(openId).then((r) => { if (r) { runs.reload(); detail.reload(); } })
                }
              >
                {commit.busy
                  ? 'Posting…'
                  : `Commit — charge ${detail.data!.membership_count} members ${money(detail.data!.total_minor, currency)}`}
              </button>
            ) : atLeast('controller') && detail.data.status === 'committed' ? (
              <button
                className="secondary small"
                disabled={reverse.busy}
                onClick={() =>
                  reverse.run(openId, 'Reversed from the back office')
                    .then((r) => { if (r) { runs.reload(); detail.reload(); } })
                }
              >
                {reverse.busy ? 'Reversing…' : 'Reverse the whole run'}
              </button>
            ) : null
          }
        >
          <ErrorBanner error={commit.error ?? reverse.error} />

          <div className="tiles">
            <Tile label="Being charged" value={String(billed.length)} note={money(detail.data.total_minor, currency)} />
            <Tile label="Prorated" value={String(prorated.length)} note="joined or left mid-period" />
            <Tile label="Not billed" value={String(skipped.length)} note="each with a reason" />
            <Tile
              label="Status"
              value={titleCase(detail.data.status)}
              note={detail.data.committed_at ? dateTime(detail.data.committed_at) : 'nothing posted yet'}
              tone={detail.data.status === 'preview' ? 'neutral' : 'good'}
            />
          </div>
        </Card>
      )}

      {openId && detail.data && (
        <Card title="Line by Line" flush>
          <table className="table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Class</th>
                <th className="num">Full dues</th>
                <th style={{ paddingLeft: 18 }}>Prorated</th>
                <th className="num">Charged</th>
                <th>Not billed because</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id}>
                  <td>
                    <strong>{i.display_name}</strong>
                    <div className="muted small">{i.account_number}</div>
                  </td>
                  <td>{i.category_name}</td>
                  <td className="num"><Money minor={i.gross_minor} currency={currency} /></td>
                  <td style={{ paddingLeft: 18 }}>
                    {i.proration_numerator !== null
                      ? `${i.proration_numerator}/${i.proration_denominator} days`
                      : ''}
                  </td>
                  <td className="num">
                    {i.skip_reason ? '—' : <Money minor={i.net_minor} currency={currency} />}
                  </td>
                  <td className="muted">{i.skip_reason ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

/** The month just gone, which is what a monthly cycle almost always wants. */
function defaultMonth(): { from: string; to: string } {
  const now = new Date();
  const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const last = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0));
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
}


// =====================================================================
// Article X — whose membership is at risk
//
// 0105 can end a membership; 0106 shows who the clock is running on. This
// is the part that has to exist before the first one is ever used, because
// `fn_forfeit_overdue` reports what it did after it has done it, and that
// is the wrong moment to learn that somebody's membership ceases on
// Thursday.
//
// Renders nothing where the club has adopted no forfeiture rule, which is
// every club until somebody answers the question in Settings.
// =====================================================================

interface WatchRow {
  member_account_id: string;
  account_number: string;
  display_name: string;
  status: string;
  suspension_notice_sent_on: string | null;
  escalation_hold_until: string | null;
  overdue_minor: number;
  days_allowed: number | null;
  days_since_notice: number | null;
  forfeits_on: string | null;
  forfeited_on: string | null;
  standing: string;
}

/** The one place the standing becomes a colour, so nothing else guesses. */
function standingTone(s: string): 'good' | 'warning' | 'serious' | 'critical' | 'neutral' {
  if (s === 'due to forfeit') return 'critical';
  if (s === 'forfeited') return 'serious';
  if (s === 'counting') return 'warning';
  if (s === 'paid') return 'good';
  return 'neutral';
}

export function ForfeitureWatch() {
  const { atLeast, currency } = useSession();
  const mayAct = atLeast('controller');
  const mayNotify = atLeast('manager');

  const watch = useApi<{
    days_after_notice: number | null;
    reinstatement_months: number | null;
    reinstatement_threshold: string | null;
    accounts: WatchRow[];
  }>('/v1/collections/forfeiture');
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const recordNotice = useMutation(async (id: string) => {
    await api(`/v1/collections/${id}/suspension-notice`, { method: 'POST', body: {} });
    setNote('Notice recorded. The clock starts today.');
    watch.reload();
  });

  // Reinstating one. Article X gives this to the Board, so the screen
  // asks for the resolution rather than a confirmation — a reinstatement
  // is somebody's vote, not somebody's click.
  const [reinstating, setReinstating] = useState<string | null>(null);
  const [motionId, setMotionId] = useState('');
  const motions = useApi<{ motions: any[] }>(
    // The club's OWN threshold, passed through — without it the database
    // answers "any carried board motion authorizes this", and the picker
    // cheerfully offers a simple majority to a club that requires
    // two-thirds. Asking with the threshold is what makes `authorizes`
    // mean anything.
    reinstating
      ? `/v1/governance/motions?body=BOARD&carried=true${
          watch.data?.reinstatement_threshold
            ? `&threshold=${encodeURIComponent(watch.data.reinstatement_threshold)}`
            : ''
        }`
      : null,
    [reinstating, watch.data?.reinstatement_threshold],
  );

  const reinstate = useMutation(async (id: string) => {
    await api(`/v1/collections/${id}/reinstate`, {
      method: 'POST',
      body: { motion_id: motionId },
    });
    setNote('Reinstated. The membership is current again.');
    setReinstating(null);
    setMotionId('');
    watch.reload();
  });

  const run = useMutation(async (confirm: boolean) => {
    const r = await api<{ forfeited?: WatchRow[]; would_forfeit?: WatchRow[] }>(
      '/v1/collections/forfeit',
      { method: 'POST', body: { confirm } },
    );
    if (confirm) {
      const n = (r.forfeited ?? []).length;
      setNote(n === 1 ? 'One membership has ceased.' : `${n} memberships have ceased.`);
      setConfirming(false);
      watch.reload();
    } else {
      setConfirming(true);
      setNote(null);
    }
    return r;
  });

  // A club that has not adopted the rule has nothing to see here, and an
  // empty table would read as "nobody is at risk" rather than "we do not
  // do this".
  if (watch.loading || !watch.data || watch.data.days_after_notice === null) return null;

  const rows = watch.data.accounts;
  const due = rows.filter((r) => r.standing === 'due to forfeit');

  return (
    <Card
      title="Memberships at Risk"
      actions={
        mayAct && due.length > 0 ? (
          confirming ? (
            <div className="row">
              <button className="secondary small" onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <button className="danger small" disabled={run.busy} onClick={() => void run.run(true)}>
                {run.busy ? 'Ending…' : `Yes — end ${due.length} membership${due.length === 1 ? '' : 's'}`}
              </button>
            </div>
          ) : (
            <button className="secondary small" disabled={run.busy} onClick={() => void run.run(false)}>
              {run.busy ? 'Checking…' : 'Run forfeiture…'}
            </button>
          )
        ) : null
      }
    >
      <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>
        This club ends a membership {watch.data.days_after_notice} days after the Treasurer's
        notice of suspension, if the debt is still unpaid. The clock starts when the notice is
        recorded — nothing starts it automatically, because the software does not know when a
        letter was posted.
      </p>

      <ErrorBanner error={watch.error ?? run.error ?? recordNotice.error ?? reinstate.error} />
      {note ? <div className="ok-banner">{note}</div> : null}
      {confirming ? (
        <div className="notice">
          {due.length} membership{due.length === 1 ? '' : 's'} will cease today. This is what
          Article X does; it is not a suspension, and reinstating one afterwards needs a vote of
          the board.
        </div>
      ) : null}

      {rows.length === 0 ? (
        <Empty>Nobody is suspended or in collections. Nothing is at risk.</Empty>
      ) : (
        <table className="table">
          <colgroup>
            <col style={{ width: '26%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '14%' }} />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th>Member</th>
              <th className="num">Owed</th>
              <th style={{ paddingLeft: 18 }}>Notice sent</th>
              <th className="num" style={{ paddingLeft: 18 }}>Day</th>
              <th style={{ paddingLeft: 18 }}>Ceases on</th>
              <th>Standing</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.member_account_id}>
                <td>
                  <strong>{r.display_name}</strong>
                  <div className="muted small">{r.account_number}</div>
                </td>
                <td className="num"><Money minor={r.overdue_minor} currency={currency} /></td>
                <td style={{ paddingLeft: 18 }}>
                  {r.suspension_notice_sent_on
                    ? date(r.suspension_notice_sent_on)
                    : <span className="muted">not recorded</span>}
                </td>
                <td className="num" style={{ paddingLeft: 18 }}>
                  {r.days_since_notice === null
                    ? <span className="muted">—</span>
                    : `${r.days_since_notice} of ${r.days_allowed}`}
                </td>
                <td style={{ paddingLeft: 18 }}>
                  {r.status === 'forfeited' && r.forfeited_on ? (
                    <>
                      {date(r.forfeited_on)}
                      <div className="muted small">ceased</div>
                    </>
                  ) : r.forfeits_on ? (
                    date(r.forfeits_on)
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  <Status tone={standingTone(r.standing)}>{r.standing}</Status>
                  {r.standing === 'held by the board' && r.escalation_hold_until && (
                    <div className="muted small">until {date(r.escalation_hold_until)}</div>
                  )}
                </td>
                <td className="num">
                  {r.suspension_notice_sent_on === null && r.status !== 'forfeited' && mayNotify ? (
                    <button
                      className="secondary small"
                      disabled={recordNotice.busy}
                      onClick={() => void recordNotice.run(r.member_account_id)}
                    >
                      Notice sent today
                    </button>
                  ) : null}

                  {r.status === 'forfeited' && mayAct ? (
                    reinstating === r.member_account_id ? (
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        <select
                          value={motionId}
                          onChange={(e) => setMotionId(e.target.value)}
                          style={{ maxWidth: 280 }}
                        >
                          <option value="">Which board resolution…</option>
                          {(motions.data?.motions ?? []).map((m: any) => (
                            <option key={m.id} value={m.id} disabled={!m.authorizes}>
                              {date(m.held_on)} — {m.subject}
                              {m.authorizes ? '' : ' (does not carry the threshold)'}
                            </option>
                          ))}
                        </select>
                        <button
                          className="small"
                          disabled={!motionId || reinstate.busy}
                          onClick={() => void reinstate.run(r.member_account_id)}
                        >
                          {reinstate.busy ? 'Reinstating…' : 'Reinstate'}
                        </button>
                        <button
                          className="secondary small"
                          onClick={() => { setReinstating(null); setMotionId(''); }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        className="secondary small"
                        onClick={() => { setReinstating(r.member_account_id); setMotionId(''); }}
                      >
                        Reinstate…
                      </button>
                    )
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
